import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ElliottWorking from '../../../../../components/ui/ElliottWorking';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ActionPill from './ActionPill';
import SectionToggle from './SectionToggle';
import { ElliottSimulationRubric } from '../types';

const fieldLabelSx = {
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'Inter',
  lineHeight: '20px',
  color: 'var(--Text-text-primary, #FDFDFD)'
} as const;

// Matches the other section titles (Lesson Content / Case Studies / Lesson Quiz).
const sectionTitleSx = {
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'Inter',
  color: 'var(--Text-text-tertiary, #868686)'
} as const;

const helperSx = { fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: 1.5 } as const;

// Editable colour-coded bullet list (green = strong, red = weak).
const BulletList: React.FC<{
  items: string[];
  onChange: (items: string[]) => void;
  tone: 'strong' | 'weak';
}> = ({ items, onChange, tone }) => {
  const palette =
    tone === 'strong'
      ? { bg: '#162016', border: '#32A032', bullet: '#5AFF50' }
      : { bg: '#201616', border: '#A03232', bullet: '#FF5A50' };

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const update = (i: number, v: string) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);

  // Word-style: backspace on an empty row deletes it and moves the cursor to the end of the previous row.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === 'Backspace' && items[i] === '' && items.length > 1) {
      e.preventDefault();
      remove(i);
      window.setTimeout(() => {
        const prev = inputRefs.current[i - 1];
        if (prev) {
          prev.focus();
          const len = prev.value.length;
          prev.setSelectionRange(len, len);
        }
      }, 0);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: '6px',
        px: '14px',
        py: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
      }}
    >
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '8px', '&:hover .cs-remove': { opacity: 1 } }}>
          <Typography sx={{ fontSize: 14, lineHeight: '20px', color: palette.bullet, flexShrink: 0 }}>•</Typography>
          <input
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            value={item}
            onChange={(e) => update(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontFamily: 'Inter',
              fontSize: 13
            }}
          />
          <CloseIcon
            className="cs-remove"
            onClick={() => remove(i)}
            sx={{ fontSize: 14, color: 'var(--Text-text-tertiary, #868686)', cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', '&:hover': { color: palette.bullet } }}
          />
        </Box>
      ))}
      <Box
        onClick={add}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', mt: '2px', cursor: 'pointer', color: 'var(--Text-text-tertiary, #868686)', '&:hover': { color: palette.bullet } }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 12, fontFamily: 'Inter' }}>Add item</Typography>
      </Box>
    </Box>
  );
};

const LiveSimulationSection: React.FC<{
  /** Real rubric from lesson.elliott.simulationRubric; null while it's still generating. */
  rubric?: ElliottSimulationRubric | null;
  /** Omit to hide the AI Reasoning pill (the published-program designer has no reasoning panel). */
  onReasoning?: () => void;
  onImprove: () => void;
  /** Fired on every manual field change — parent debounces + persists (PATCH elliott-section). */
  onChange?: (rubric: { brief: string; skill: string; strongPoints: string[]; weakPoints: string[] }) => void;
  /** Admin on/off toggle for the whole simulation step (absent flag on the data = enabled). */
  enabled?: boolean;
  /** Omit to hide the toggle. Fired with the new value — parent persists immediately. */
  onToggleEnabled?: (enabled: boolean) => void;
}> = ({ rubric, onReasoning, onImprove, onChange, enabled = true, onToggleEnabled }) => {
  // Never seed placeholder copy into the editable fields - that made every
  // not-yet-generated lesson show the same fake scenario, indistinguishable from
  // real content. Fields stay empty until a real rubric arrives; while it's null
  // we render an explicit "still generating" state instead of editable inputs.
  const isGenerating = !rubric;
  const [brief, setBrief] = useState(rubric?.brief || '');
  const [skill, setSkill] = useState(rubric?.skill || '');
  const [strong, setStrong] = useState<string[]>(rubric?.strongPoints || []);
  const [weak, setWeak] = useState<string[]>(rubric?.weakPoints || []);

  // Re-sync when the rubric arrives or is regenerated (section-scoped improve / cascade).
  useEffect(() => {
    if (!rubric) return;
    setBrief(rubric.brief || '');
    setSkill(rubric.skill || '');
    setStrong(rubric.strongPoints || []);
    setWeak(rubric.weakPoints || []);
  }, [rubric]);

  const emit = (patch: Partial<{ brief: string; skill: string; strongPoints: string[]; weakPoints: string[] }>) => {
    onChange?.({ brief, skill, strongPoints: strong, weakPoints: weak, ...patch });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header — title + description stacked on the left; CTAs (match Lesson Content) top-right */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 240 }}>
          <Typography sx={sectionTitleSx}>Roleplay</Typography>
          <Typography sx={helperSx}>
            Roleplay assesses a learner&apos;s understanding by presenting a realistic situation that they must work
            through with Elliott. It is an effective way to verify that learners have not only absorbed the Lesson Body
            but can also apply their knowledge confidently in practical, real-world situations.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {enabled && onReasoning && (
            <ActionPill
              icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Elliott's reasoning"
              onClick={onReasoning}
            />
          )}
          {enabled && (
            <ActionPill
              icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
              label="Improve with Elliott"
              onClick={onImprove}
            />
          )}
          {onToggleEnabled && (
            <SectionToggle
              enabled={enabled}
              onToggle={onToggleEnabled}
              title="Turn the Scenario Testing step on or off for this lesson"
              label="Roleplay"
            />
          )}
        </Box>
      </Box>

      {!enabled && (
        <Typography sx={{ ...helperSx, fontStyle: 'italic' }}>
          Roleplay is turned off for this lesson. Learners will move straight from the case studies to the quiz. Turn it
          back on to restore this step (your brief and rubric below are kept).
        </Typography>
      )}

      {enabled && isGenerating && (
        <ElliottWorking message="Elliott is preparing the Roleplay for this lesson. The Roleplay will appear here shortly, ready for you to edit." />
      )}

      {enabled && !isGenerating && (
        <>
      {/* Simulation Brief */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={fieldLabelSx}>Roleplay brief:</Typography>
        <Box
          component="textarea"
          value={brief}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setBrief(e.target.value);
            emit({ brief: e.target.value });
          }}
          rows={3}
          sx={{
            width: '100%',
            background: 'rgba(0,0,0,0.9)',
            border: '1px solid var(--Stroke-Border-border-highlight, #868686)',
            borderRadius: '6px',
            px: 1.5,
            py: 1,
            resize: 'vertical',
            minHeight: 76,
            color: 'var(--Text-text-primary, #FDFDFD)',
            fontFamily: 'Inter',
            fontSize: 14,
            lineHeight: 1.42,
            outline: 'none',
            '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
          }}
        />
      </Box>

      {/* Skill Being Assessed */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography sx={fieldLabelSx}>Skill being assessed:</Typography>
        <Box
          component="input"
          value={skill}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSkill(e.target.value);
            emit({ skill: e.target.value });
          }}
          sx={{
            width: '100%',
            background: 'rgba(0,0,0,0.7)',
            border: '1px solid var(--Stroke-Border-border-highlight, #868686)',
            borderRadius: '6px',
            px: 1.5,
            py: 1.25,
            color: 'var(--Text-text-primary, #FDFDFD)',
            fontFamily: 'Inter',
            fontSize: 14,
            outline: 'none',
            '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
          }}
        />
      </Box>

      {/* Strong response includes */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={helperSx}>Strong response includes:</Typography>
        <BulletList
          items={strong}
          onChange={(items) => {
            setStrong(items);
            emit({ strongPoints: items });
          }}
          tone="strong"
        />
      </Box>

      {/* Weak response includes */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={helperSx}>Weak response includes:</Typography>
        <BulletList
          items={weak}
          onChange={(items) => {
            setWeak(items);
            emit({ weakPoints: items });
          }}
          tone="weak"
        />
      </Box>
        </>
      )}
    </Box>
  );
};

export default LiveSimulationSection;
